/**
 * Filling Activity Attribute Rules
 *
 * Defines attribute behavior for filling activities across all statuses
 * Now using shared attribute library for DRY code
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig } from "../types"
import {
  pressureForStarted,
  pressureForCompleted,
  pressureHidden,
  temperatureForStarted,
  temperatureForCompleted,
  temperatureHidden,
  quantityForScheduled,
  quantityHidden,
  fillLevelForStarted,
  fillLevelHidden,
  siteForScheduled,
  siteInherited,
  batchForScheduled,
  batchInherited,
  getDefaultAsset,
} from "../shared"
import { findAttributeValue } from "../shared/helpers"

/**
 * Asset configuration for Scheduled status
 * Editable during creation, read-only when editing existing activity
 */
const assetConfigForScheduled: AttributeConfig = {
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  // Only editable when creating a new activity (temp ID), not when editing existing
  editable: (ctx) => ctx.activity.id.startsWith("temp"),
  required: false,
  helpText: "Select an asset for this activity",
  order: 1, // First field
  defaultValue: (ctx) => {
    // Default to first order asset if none selected
    return getDefaultAsset(ctx)
  },
}

/**
 * Asset configuration for non-scheduled statuses (Started/Completed)
 * Inherited chronologically from previous status, read-only
 */
const assetConfigInherited = (fromEventKind: gqlTypes.ActivityEventKind): AttributeConfig => ({
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  editable: false,
  required: false,
  helpText: "Asset for this activity (use Edit Asset button to change)",
  order: 1,
  defaultValue: (ctx) => {
    // Inherit asset from the specified previous status
    const assetValue = findAttributeValue(ctx, "asset", [fromEventKind])
    if (assetValue) return assetValue

    // Fallback to first order asset if not found in previous status
    return getDefaultAsset(ctx)
  },
})

/**
 * Tank level for Completed - calculated only (NOT IoT)
 * Calculator is now in baseFillLevelConfig (shared across all activities)
 */
const fillLevelForCompletedCalculated: AttributeConfig = {
  ...fillLevelForStarted(),
  helpText: "Final tank level (auto-calculated from pressure × temperature × volume)",
}

/**
 * Quantity for Completed - calculated only (NOT IoT)
 * Calculated as: Completed Tank Level - Started Tank Level
 */
const quantityForCompletedCalculated: AttributeConfig = {
  attribute: gqlTypes.ActivityAttributeKind.Quantity,
  label: "Fill Quantity (kg)",
  visibility: "calculated",
  editable: true,
  required: false,
  helpText: "Auto-calculated from (completed tank level - started tank level)",
  order: 10, // Order for quantity
  // NO IoT source - calculated from tank levels only
  calculator: async (ctx) => {
    // Get started tank level from saved attributes
    const startedFillLevelValue = ctx.activity.attributes?.find(
      (a) =>
        a.attribute === gqlTypes.ActivityAttributeKind.FillLevel && a.event_kind === gqlTypes.ActivityEventKind.Started
    )?.value

    const startedFillLevel = startedFillLevelValue ? parseFloat(startedFillLevelValue) : null

    // Get completed tank level from currentValues (reactive) or saved attributes (fallback)
    const completedFillLevel = parseFloat(
      (ctx as any).currentValues?.fill_level ||
        ctx.activity.attributes?.find(
          (a) =>
            a.attribute === gqlTypes.ActivityAttributeKind.FillLevel &&
            a.event_kind === gqlTypes.ActivityEventKind.Completed
        )?.value ||
        "0"
    )

    // If no started tank level exists, use the completed tank level as the quantity
    // Otherwise, calculate the difference
    const quantity = startedFillLevel !== null ? Math.abs(completedFillLevel - startedFillLevel) : completedFillLevel

    return Math.abs(quantity).toFixed(2)
  },
  calculatorDependencies: [gqlTypes.ActivityAttributeKind.FillLevel],
  validator: (value) => {
    if (!value) return { valid: true }
    const num = parseFloat(value)
    if (isNaN(num) || num < 0) {
      return { valid: false, error: "Quantity must be positive" }
    }
    return { valid: true }
  },
}

/**
 * Attribute rules for Filling activities
 * Organized by status: Scheduled → Started → Completed
 */
export const FILLING_RULES: Record<gqlTypes.ActivityEventKind, AttributeConfig[]> = {
  [gqlTypes.ActivityEventKind.Pending]: [],

  /**
   * SCHEDULED STATUS
   * Planning phase - set quantity, optionally site and batch
   */
  [gqlTypes.ActivityEventKind.Scheduled]: [
    assetConfigForScheduled,
    {
      ...quantityForScheduled("filling"),
      order: 2,
      // Override validator to include warning for large quantities
      validator: (value) => {
        if (!value) {
          return { valid: false, error: "Fill quantity is required" }
        }
        const num = parseFloat(value)
        if (isNaN(num)) {
          return { valid: false, error: "Must be a valid number" }
        }
        if (num <= 0) {
          return { valid: false, error: "Quantity must be positive" }
        }
        if (num > 1000) {
          return {
            valid: true,
            warning: "Quantity exceeds typical trailer capacity (1000kg)",
          }
        }
        return { valid: true }
      },
    },
    { ...siteForScheduled("filling"), order: 3 },
    { ...batchForScheduled(), order: 4 },
    { ...pressureHidden, order: 5 },
    { ...temperatureHidden, order: 6 },
    { ...fillLevelHidden, order: 7 },
  ],

  /**
   * STARTED STATUS
   * Filling in progress - site/batch inherited (editable if not set), record current readings
   */
  [gqlTypes.ActivityEventKind.Started]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Scheduled),
    { ...siteInherited("filling", gqlTypes.ActivityEventKind.Scheduled, true), order: 2 }, // Editable if not inherited
    { ...batchInherited([gqlTypes.ActivityEventKind.Scheduled], true), order: 3 }, // Editable if not inherited
    { ...pressureForStarted(), order: 4 }, // Pressure and temperature on same row, IoT-enabled
    { ...temperatureForStarted(), order: 5 }, // IoT-enabled
    { ...fillLevelForStarted(), order: 6 }, // Tank level right after P/T
    { ...quantityHidden, order: 7 },
  ],

  /**
   * COMPLETED STATUS
   * Filling complete - record final readings, calculate quantity
   */
  [gqlTypes.ActivityEventKind.Completed]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Started),
    {
      ...siteInherited("filling", gqlTypes.ActivityEventKind.Started),
      order: 2,
      // Fallback to Scheduled if not found in Started
      defaultValue: (ctx) => {
        const startedSite = ctx.activity.attributes?.find(
          (attr) =>
            attr.attribute === gqlTypes.ActivityAttributeKind.Site &&
            attr.event_kind === gqlTypes.ActivityEventKind.Started
        )
        if (startedSite?.site?.id) return startedSite.site.id

        const scheduledSite = ctx.activity.attributes?.find(
          (attr) =>
            attr.attribute === gqlTypes.ActivityAttributeKind.Site &&
            attr.event_kind === gqlTypes.ActivityEventKind.Scheduled
        )
        return scheduledSite?.site?.id || null
      },
    },
    {
      ...batchInherited([gqlTypes.ActivityEventKind.Started, gqlTypes.ActivityEventKind.Scheduled], false),
      order: 3,
    }, // Locked once completed
    // IoT-enabled sensor readings - P/T on same row
    { ...pressureForCompleted(), order: 4 }, // IoT-enabled
    { ...temperatureForCompleted(), order: 5 }, // IoT-enabled
    { ...fillLevelForCompletedCalculated, order: 6 }, // Tank level right after P/T
    { ...quantityForCompletedCalculated, order: 7 }, // Calculated only, NOT IoT
  ],
}
